import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Loader from 'components/Loader';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import './DocumentRequestsTab.scss';

const TTL_MIN_DAYS = 1;
const TTL_MAX_DAYS = 365;

const isValidUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

const DocumentRequestsTab = ({ settingsStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();

  useEffect(() => {
    if (!settingsStore.isInitialized) {
      settingsStore.getApplicationProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsStore.isInitialized]);

  useEffect(() => {
    if (settingsStore.isInitialized) {
      settingsStore.resetSettingsStore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (!settingsStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  const landingUrl = settingsStore.documentRequestLandingUrl || '';
  const tokenTtlDays = settingsStore.documentRequestTokenTtlDays || '';
  const initialProperties = settingsStore.initialDocumentRequestProperties;

  const ttlNumber = Number(tokenTtlDays);
  const isLandingUrlValid = !landingUrl || isValidUrl(landingUrl);
  const isTokenTtlValid =
    !tokenTtlDays || (Number.isInteger(ttlNumber) && ttlNumber >= TTL_MIN_DAYS && ttlNumber <= TTL_MAX_DAYS);

  const isLandingUrlChanged = landingUrl !== initialProperties.landingUrl;
  const isTokenTtlChanged = tokenTtlDays !== initialProperties.tokenTtlDays;
  const hasChanges = isLandingUrlChanged || isTokenTtlChanged;

  // Backend rejects blank values (@NotBlank / @NotNull) — a changed field must not be emptied
  const isSaveDisabled =
    !hasChanges ||
    !isLandingUrlValid ||
    !isTokenTtlValid ||
    (isLandingUrlChanged && !landingUrl) ||
    (isTokenTtlChanged && !tokenTtlDays) ||
    settingsStore.isLoading;

  return (
    <div className={'document-requests-wrapper'}>
      <div className={'document-requests-section'}>
        <h3 className={'document-requests-title'}>{i18n.getMessage('settings.documentRequests.title')}</h3>
        <p className={'document-requests-description'}>{i18n.getMessage('settings.documentRequests.description')}</p>
        <div className={'document-requests-form'}>
          <Input
            className={'document-requests-input'}
            name={'document_request_landing_url'}
            label={i18n.getMessage('settings.documentRequests.landingUrl.label')}
            placeholder={i18n.getMessage('settings.documentRequests.landingUrl.placeholder')}
            value={landingUrl}
            onChange={({ target: { value } }) => settingsStore.setDocumentRequestLandingUrl(value)}
            error={!isLandingUrlValid ? i18n.getMessage('settings.documentRequests.landingUrl.error') : undefined}
            disabled={settingsStore.isLoading}
          />
          <p className={'document-requests-field-hint'}>
            {i18n.getMessage('settings.documentRequests.landingUrl.hint')}
          </p>
          <Input
            className={'document-requests-input document-requests-input-ttl'}
            name={'document_request_token_ttl_days'}
            label={i18n.getMessage('settings.documentRequests.tokenTtlDays.label')}
            placeholder={i18n.getMessage('settings.documentRequests.tokenTtlDays.placeholder')}
            value={tokenTtlDays}
            onChange={({ target: { value } }) => settingsStore.setDocumentRequestTokenTtlDays(value.replace(/\D/g, ''))}
            error={!isTokenTtlValid ? i18n.getMessage('settings.documentRequests.tokenTtlDays.error') : undefined}
            disabled={settingsStore.isLoading}
          />
          <p className={'document-requests-field-hint'}>
            {i18n.getMessage('settings.documentRequests.tokenTtlDays.hint')}
          </p>
        </div>
      </div>
      <div className={'document-requests-actions-wrapper'}>
        <Button
          className={'document-requests-button'}
          size={'medium'}
          type={'primary'}
          roleType={'button'}
          onClick={settingsStore.updateDocumentRequestProperties}
          isDisabled={isSaveDisabled}
        >
          {settingsStore.isLoading ? <Loader /> : i18n.getMessage('settings.documentRequests.button.saveChanges')}
        </Button>
      </div>
    </div>
  );
};

DocumentRequestsTab.propTypes = {
  settingsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  settingsStore: stores.settingsStore
}))(observer(DocumentRequestsTab));
